/**
 * Rollo garment identity — the durable-identity QR codec + the canonical garment↔placement pairing.
 *
 * Two pure, IO-free pieces that MUST be the single source of truth shared by the Phase-1 label encoder
 * (dashboard) and the Phase-3 scan→settle wiring (rest-api). If those two sides ever compute "which
 * physical garment is rank k" differently, a garment strands half-pressed — exactly the failure the Rollo
 * plan (docs/rollo-barcode-garment-tracking-plan-v3.md §2, §4) exists to prevent. So the rule lives here,
 * once, golden-tested, and both callers import it.
 *
 * ── Placement note (charter) ──
 * `@fsp/contracts` is "schemas, no logic" (§4.2). This module is a deliberate, narrow exception: it is the
 * *serialization and canonical identity* of a contract-defined entity (an order-line garment), it is pure
 * (no IO, no error-translation, deterministic), and `@fsp/contracts` is the ONLY workspace package the
 * dashboard imports — so it is the one home reachable by both the encoder and the settle-wire without
 * adding a new package. If a dedicated `@fsp/garment-identity` package is preferred, this file moves
 * verbatim.
 *
 * ── Grounded invariant this module encodes (verified against the codebase 2026-07-25) ──
 * A line printed front+back is TWO batch-item rows (SK `ITEM#store#order#line#location`) but ONE physical
 * garment per copy. Per-placement copy count is ALWAYS the line quantity — it is computed once per line
 * (`batches.ts:405`) and written into every placement + SP work entry, and reissue keys by line
 * (`batches.ts:1140-1186`), so front-copy-k and back-copy-k ALWAYS refer to the same physical shirt.
 * A refund is line-level and TRAILING: `owedConsumeQty = CANCELLED ? 0 : max(0, min(batchedQty,
 * currentQty ?? batchedQty))` (`garment-settle.ts:75-81`), read identically by both placements, and the
 * per-copy press guard refuses `copyIndex >= owed` (`batches.ts:1698`). Hence: garment rank r (1-based) ⇔
 * copyIndex r-1 on every placement, and the surviving ranks 1..owed stay identical across placements — no
 * divergence, no half-pressed strand. `copyIndex` is the stable ordinal (do NOT use the position-derived
 * global `index`).
 */
import { z } from 'zod';
// ─────────────────────────────────────────────────────────────────────────────
// Codec — the QR payload `v1|storeId|orderId|lineId|owedRank`
// ─────────────────────────────────────────────────────────────────────────────
/** Current payload namespace/version tag. A scanned code not starting with this decodes to `null`. */
export const GARMENT_CODE_VERSION = 'v1';
/** Field delimiter. Ids must not contain it (guarded at encode). */
const DELIM = '|';
/** Reject absurd input early (a QR realistically encodes well under this). */
const MAX_CODE_LENGTH = 256;
/** The decoded, validated garment code. `owedRank` is the 1-based line-level ordinal (= pairing `rank`). */
export const GarmentCodeSchema = z.object({
    version: z.literal(GARMENT_CODE_VERSION),
    storeId: z.string().min(1),
    orderId: z.string().min(1),
    lineId: z.string().min(1),
    owedRank: z.number().int().positive(),
});
/**
 * Encode a physical garment's durable identity into the QR payload string.
 * Throws on inputs that would produce an unparseable/ambiguous code (empty id, id containing the delimiter,
 * non-positive-integer rank) — these are caller bugs, never operator input.
 */
export function encodeGarmentCode(input) {
    for (const [field, value] of [
        ['storeId', input.storeId],
        ['orderId', input.orderId],
        ['lineId', input.lineId],
    ]) {
        if (!value)
            throw new Error(`encodeGarmentCode: ${field} is empty`);
        if (value.includes(DELIM)) {
            throw new Error(`encodeGarmentCode: ${field} contains the "${DELIM}" delimiter`);
        }
    }
    if (!Number.isInteger(input.owedRank) || input.owedRank < 1) {
        throw new Error('encodeGarmentCode: owedRank must be a positive integer');
    }
    return [
        GARMENT_CODE_VERSION,
        input.storeId,
        input.orderId,
        input.lineId,
        String(input.owedRank),
    ].join(DELIM);
}
/**
 * Decode a scanned string into a garment code, or `null` if it is not a well-formed current-version code.
 * NEVER throws — a scanner sees foreign QRs (Shopify packing slips, product codes, random labels) as
 * routine input, and the caller renders an "unrecognized code" state on `null`. The `v1|` version tag is
 * what rejects foreign codes; an unknown/future version also returns `null` (→ "reprint from current app").
 */
export function decodeGarmentCode(raw) {
    if (typeof raw !== 'string')
        return null;
    const trimmed = raw.trim();
    if (trimmed.length === 0 || trimmed.length > MAX_CODE_LENGTH)
        return null;
    const parts = trimmed.split(DELIM);
    if (parts.length !== 5)
        return null;
    const [version, storeId, orderId, lineId, rankStr] = parts;
    if (version !== GARMENT_CODE_VERSION)
        return null; // foreign or newer-scheme code
    // digits only — no sign, decimal, exponent, or whitespace (the `=== 5` split guarantees rankStr, the
    // undefined check just satisfies noUncheckedIndexedAccess)
    if (rankStr === undefined || !/^\d+$/.test(rankStr))
        return null;
    const owedRank = Number(rankStr);
    const result = GarmentCodeSchema.safeParse({
        version,
        storeId,
        orderId,
        lineId,
        owedRank,
    });
    return result.success ? result.data : null;
}
/**
 * Batched garment count for a line — a valid non-negative integer, else 0. The trusted path always passes
 * a frozen integer line quantity; guarding a non-finite/non-integer `batchedQty` (Infinity/NaN/fractional)
 * to 0 keeps `garmentsForLine` from hanging and keeps it consistent with `resolveGarment` (both derive
 * their range from here, so they can never disagree on a garbage input).
 */
function batchedCount(line) {
    return Number.isInteger(line.batchedQty) && line.batchedQty > 0 ? line.batchedQty : 0;
}
/**
 * Build one garment: its rank + every placement at copyIndex `rank-1`, all sharing the line-level `owed`
 * flag. The SINGLE construction used by both `garmentsForLine` and `resolveGarment`, so the two entry
 * points are physically incapable of disagreeing about what garment rank k is.
 */
function buildGarment(line, rank, owed) {
    const isOwed = rank <= owed;
    return {
        rank,
        owed: isOwed,
        placements: line.placements.map((p) => ({
            location: p.location,
            ...(p.method !== undefined ? { method: p.method } : {}),
            copyIndex: rank - 1,
            owed: isOwed,
        })),
    };
}
/**
 * How many garments of the line are still owed. Mirrors `owedConsumeQty` (`garment-settle.ts:75-81`) for
 * every valid input — it must agree with the per-copy press guard (`batches.ts:1698`, refuses
 * `copyIndex >= owed`) or a label's do-not-press would diverge from what the press station accepts.
 * `currentQty` is an integer by schema; a non-integer `batchedQty` is guarded to 0 via `batchedCount`.
 */
export function lineOwed(line) {
    if (line.cancelled)
        return 0;
    const total = batchedCount(line);
    return Math.max(0, Math.min(total, line.currentQty ?? total));
}
/**
 * The canonical garment list for a line: one entry per batched garment (rank 1..batchedQty), each mapping
 * to copyIndex `rank-1` on every placement, flagged owed iff `rank <= lineOwed`. This is what the label
 * encoder enumerates (one label per garment) and what the scan resolver reconstructs from the live line.
 */
export function garmentsForLine(line) {
    const owed = lineOwed(line);
    const total = batchedCount(line);
    const garments = [];
    for (let rank = 1; rank <= total; rank += 1) {
        garments.push(buildGarment(line, rank, owed));
    }
    return garments;
}
/**
 * Resolve a single garment by its 1-based rank (the QR's `owedRank`) against the live line.
 * Returns `null` if the rank is out of the batched range (rank < 1 or rank > batchedQty) — a "not on this
 * batch / surplus copy" scan state, distinct from an in-range-but-not-owed garment (returned with
 * `owed:false` → the do-not-press / refunded state).
 */
export function resolveGarment(line, rank) {
    if (!Number.isInteger(rank) || rank < 1 || rank > batchedCount(line))
        return null;
    return buildGarment(line, rank, lineOwed(line));
}
//# sourceMappingURL=garment-identity.js.map